import { inject } from "@angular/core";
import { SignalSubscriptionImpl } from "../../data-subscription";
import { CabinetTableView } from "../../../../models/types/cabinet.types";
import { BudgetStoreService } from "../../../../services/store/budget/budget.store.service";
import { format, subMilliseconds, subMonths, subWeeks } from "date-fns";
import type {
  BudgetHistoryRow,
  BudgetIntervalType,
  TransactionAmountsTime,
} from "@bitwarden/web-vault/app/models/types/budget.types";
import { BudgetUtils } from "@bitwarden/web-vault/app/models/view/budget/budget.utils";
import type { BudgetView } from "@bitwarden/web-vault/app/models/view/budget/budget.view";
import type { TableParent } from "@bitwarden/web-vault/app/models/types/table.types";

export type TableSource = CabinetTableView;

const HISTORY_PERIOD_COUNT = 3;

export class BudgetHistoryTableSubscription extends SignalSubscriptionImpl<BudgetHistoryRow[]> {
  protected budgetStoreService: BudgetStoreService = inject(BudgetStoreService);
  protected parent: TableParent;

  get source() {
    const budget = this.getSelectedBudget();
    if (!budget) {
      return [];
    }

    return this.buildPeriodRows(budget, this.getBudgetInterval(budget));
  }

  protected getSelectedBudget(): BudgetView | null {
    // `in` narrows TableParent to the union member that exposes latestBudget
    // (BudgetDetailsComponent) and type-checks the call against its real signature —
    // so a parent without latestBudget is a compile error, not a silent empty table.
    // Replaces an untyped structural cast (`parent as { latestBudget?: ... }`).
    return "latestBudget" in this.parent ? (this.parent.latestBudget() ?? null) : null;
  }

  /** History is always shown in the budget's own cadence. */
  protected getBudgetInterval(budget: BudgetView): BudgetIntervalType {
    return budget.interval;
  }

  private buildPeriodRows(budget: BudgetView, interval: BudgetIntervalType): BudgetHistoryRow[] {
    const rows: BudgetHistoryRow[] = [];
    const transactionsByCat = this.budgetStoreService.budgetTransactions.sortedCollection();
    const categoryIds = BudgetUtils.getCategoryIdsInBudget(budget);
    // History is shown in the budget's own currency; the budget amount itself is already native.
    const budgetAmount = budget.amount ?? 0;
    // Reconvert each transaction from core back to the budget currency at its own date, so a
    // same-currency spend is exact (no single-rate round-trip drift).
    const convert = this.budgetStoreService.getBudgetCurrencyConverter(budget);
    const currentStart = new Date(BudgetUtils.getIntervalStartForBudget(budget, interval));

    // Show the 3 prior completed periods (excludes the in-progress current one).
    for (let periodOffset = 1; periodOffset <= HISTORY_PERIOD_COUNT; periodOffset++) {
      const intervalStart = this.shiftInterval(currentStart, interval, -periodOffset);
      const nextIntervalStart = this.shiftInterval(currentStart, interval, 1 - periodOffset);
      const intervalEnd = subMilliseconds(nextIntervalStart, 1);
      const actual = this.getActualAmount(
        categoryIds,
        transactionsByCat,
        intervalStart,
        intervalEnd,
        convert,
      );
      const variance = budgetAmount + actual;

      rows.push({
        month: this.getPeriodLabel(intervalStart, intervalEnd, interval),
        budget: budgetAmount,
        actual,
        variance,
        status: variance >= 0 ? "UNDERSPENT" : "OVERSPENT",
      });
    }

    return rows;
  }

  private shiftInterval(date: Date, interval: BudgetIntervalType, amount: number): Date {
    return interval === "weekly" ? subWeeks(date, -amount) : subMonths(date, -amount);
  }

  private getActualAmount(
    categoryIds: string[],
    transactionsByCat: Map<string, TransactionAmountsTime[]>,
    intervalStart: Date,
    intervalEnd: Date,
    convert: (coreAmount: number, date: Date) => number,
  ): number {
    let actual = 0;
    for (const categoryId of categoryIds) {
      const transactions = transactionsByCat.get(categoryId) ?? [];
      for (const transaction of transactions) {
        if (
          transaction.timeStamp >= intervalStart.getTime() &&
          transaction.timeStamp <= intervalEnd.getTime()
        ) {
          actual += convert(transaction.amount, new Date(transaction.timeStamp));
        }
      }
    }
    return actual;
  }

  private getPeriodLabel(
    intervalStart: Date,
    intervalEnd: Date,
    interval: BudgetIntervalType,
  ): string {
    return interval === "weekly"
      ? `${format(intervalStart, "d MMM")} - ${format(intervalEnd, "d MMM yyyy")}`
      : format(intervalStart, "MMMM yyyy");
  }
}
